'use client'

import DownloadWithBasket from '@/components/icons/download-with-basket'
import { Skeleton } from '@/components/ui/skeleton'
import { downloadFile } from '@/helpers/downloads'
import { htmlClassName, normalizeHtml } from '@/helpers/html'
import { cn } from '@/lib/utils'
import apiFetch from '@/store/helpers/api-fetch'
import mimeToExtension from '@/store/helpers/mime-to-extension'
import { useEffect, useState } from 'react'
import useSWR from 'swr'

export default function ChampionshipRules({ params }) {
  const [rules, setRules] = useState([])

  const { data: championshipRules, isLoading: championshipRulesLoading } =
    useSWR(
      {
        url: `community/${params.communitySlug}/championship/${params.championshipSlug}/ruleset`,
      },
      apiFetch,
    )

  const { data: downloadableRules, isLoading: downloadableRulesLoading } =
    useSWR(
      {
        url: `community/${params.communitySlug}/championship/${params.championshipSlug}/file`,
        searchParams: {
          where: {
            usageHint: 'ruleset',
          },
        },
      },
      apiFetch,
    )

  useEffect(() => {
    if (championshipRulesLoading || downloadableRulesLoading) return

    //if the rule is a file, recognised by the headingText and descriptionHtml being 'fileItem', inject the file into the rule
    let dwnloadRulesIndexToInject = 0

    const arrayRules = Array.isArray(championshipRules)
      ? championshipRules
      : [championshipRules]

    if (championshipRules && downloadableRules) {
      const mappedRules = arrayRules.map((mappedRule) => {
        //inject files into rules
        if (mappedRule.headingText === 'fileItem') {
          mappedRule.file =
            downloadableRules[dwnloadRulesIndexToInject]?.fileUrl
          mappedRule.fileId = downloadableRules[dwnloadRulesIndexToInject]?.id
          mappedRule.headingText =
            downloadableRules[dwnloadRulesIndexToInject]?.originalFilename
          mappedRule.descriptionHtml =
            downloadableRules[dwnloadRulesIndexToInject]?.mimetype

          dwnloadRulesIndexToInject++
        }
        return mappedRule
      })

      mappedRules?.sort((a, b) => a.displayOrder - b.displayOrder)

      setRules(mappedRules)
    }
  }, [championshipRules, downloadableRules])

  async function downloadRuleset(rule) {
    await downloadFile({
      s3Url: rule.file,
      name: rule.headingText.split('.')[0],
      ext: mimeToExtension(rule.descriptionHtml),
    })
  }

  return (
    <div className="screen-grid *:grid-indent mb-10 mt-5 w-full desktop-xs:mb-[60px] desktop-xs:mt-10">
      {championshipRulesLoading || downloadableRulesLoading ? (
        <Skeleton className="h-[470px] w-full" />
      ) : (
        <div className="flex flex-col">
          <h4 className="mb-4 text-h-4 font-semibold">Rules</h4>
          {rules?.length > 0 ? (
            rules.map((ruleset, index) => (
              <div
                key={ruleset.id}
                className="col-span-full flex flex-col rounded py-2"
              >
                <div className="flex justify-between">
                  <div className="flex flex-col">
                    {ruleset.headingText?.trim().toLowerCase() !== 'rules' && (
                      <h4 className="text-h-4 font-bold">
                        {ruleset.headingText || `Rule ${index + 1}`}
                      </h4>
                    )}
                    {ruleset.descriptionHtml && (
                      <div
                        className={cn(
                          htmlClassName,
                          'mt-5 text-sm text-neutral-blue-500 lg:text-base',
                        )}
                        dangerouslySetInnerHTML={{
                          __html: normalizeHtml(ruleset.descriptionHtml),
                        }}
                      />
                    )}
                  </div>
                  {ruleset.file && (
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2">
                        <button onClick={() => downloadRuleset(ruleset)}>
                          <DownloadWithBasket className="size-8 rotate-90 cursor-pointer stroke-success" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="divide mt-4 h-px bg-neutral-blue-700" />
              </div>
            ))
          ) : (
            <p className="col-span-full">
              No rules specified for this championship.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
